import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiLongRunningRequestTimeoutMs } from "../config";
import useApiHelper from "../hooks/useApiHelper";
import { IInboxConversationRow, IInboxPartContext, IInboxSyncResult, IInboxThread } from "../models/messagesInbox/IInboxApi";
import { IInboxConversation } from "../models/messagesInbox/IInboxConversation";
import { DefaultResponseWithData } from "../models/system";
import { buildSampleConversations } from "../stubs/messagesInboxSamples";
import apiConfig from "../utils/apiConfig";
import { conversationFromRow } from "../utils/inboxApiMapping";

/** How far back the list looks on eBay (the API's window). */
export const INBOX_DAYS_TO_SEARCH = 14;

export const messagesInboxKeys = {
  all: ["messagesInbox"] as const,
  conversations: (live: boolean) => ["messagesInbox", live ? "conversations" : "sample"] as const,
  thread: (conversationId?: string) => ["messagesInbox", "thread", conversationId] as const,
  partContext: (partId?: string, buyer?: string, orderNumber?: string) => ["messagesInbox", "partContext", partId, buyer, orderNumber] as const,
  /**
   * Prefix of every partContext key for the part, whoever the buyer is. Invalidate with this: React
   * Query only matches a prefix, and partContext(partId) with its trailing undefineds matches nothing.
   */
  partContextAll: (partId: string) => ["messagesInbox", "partContext", partId] as const,
};

/** Conversation ids are opaque ("ebay:123:abc", "partpro:9"), so they're always encoded in a URL. */
export const conversationPath = (conversationId: string) => `/messagesinbox/conversations/${encodeURIComponent(conversationId)}`;

async function unwrap<T>(request: Promise<DefaultResponseWithData<T>>): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message);
  return res.data;
}

export interface IInboxList {
  conversations: IInboxConversation[];
  /** The API's word on the list, worth showing when it's empty ("not synced yet"). */
  message?: string;
}

/**
 * The inbox list. Live (the "Messages Inbox" feature flag): the API, reading the conversations the
 * 10-minute sync keeps in Wilma – a quick database read. Otherwise (localhost only) the sample
 * conversations, with no API calls at all.
 */
export default function useMessagesInbox(live: boolean, componentName = "useMessagesInbox", replyWindowHours = 24) {
  const { get } = useApiHelper(apiConfig(componentName));
  // The sync itself reads a few days of eBay and PartPro, so Refresh can take longer than a plain call.
  const { post: postLong } = useApiHelper({ ...apiConfig(`${componentName}.sync`), timeoutMs: apiLongRunningRequestTimeoutMs });
  const queryClient = useQueryClient();

  const conversations = useQuery<IInboxList>(
    [...messagesInboxKeys.conversations(live), replyWindowHours],
    async () => {
      if (!live) return { conversations: buildSampleConversations() };
      const res = await get<DefaultResponseWithData<IInboxConversationRow[]>>(`/messagesinbox/conversations?daysToSearch=${INBOX_DAYS_TO_SEARCH}`);
      if (!res.success) throw new Error(res.message);
      return { conversations: (res.data ?? []).map((row) => conversationFromRow(row, replyWindowHours)), message: res.message };
    },
    live ? { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 } : { staleTime: Infinity, refetchOnWindowFocus: false }
  );

  /**
   * Refresh: asks the API to pull anything new from eBay and PartPro into Wilma now (or be told it
   * did a moment ago), then reloads the list, the open thread and the part context. The result is
   * for syncNotice(); the list reloads whatever it said.
   */
  const sync = useMutation(() => postLong<Record<string, never>, DefaultResponseWithData<IInboxSyncResult | null>>("/messagesinbox/sync", {}), {
    onSettled: () => queryClient.invalidateQueries(messagesInboxKeys.all),
  });

  const refresh = () => (live ? sync.mutate() : queryClient.invalidateQueries(messagesInboxKeys.all));

  return { conversations, refresh, syncing: sync.isLoading, lastSync: sync.data, lastSyncError: sync.error };
}

/** The open conversation's messages, oldest first (live only). */
export function useMessagesInboxThread(conversationId: string | undefined, enabled: boolean, componentName = "useMessagesInboxThread") {
  const { get } = useApiHelper(apiConfig(componentName));

  const thread = useQuery<IInboxThread>(
    messagesInboxKeys.thread(conversationId),
    () => unwrap(get<DefaultResponseWithData<IInboxThread>>(`${conversationPath(conversationId as string)}/messages`)),
    { enabled: enabled && !!conversationId, staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 }
  );

  return { thread };
}

/**
 * Part, donor and order for the side panel (live only). The part is shared by every conversation
 * about its listing, but the order belongs to one buyer. The conversation's buyer (and, for PartPro,
 * its order number) go with the request so the API only returns the order to the conversation it
 * belongs to.
 */
export function useMessagesInboxPartContext(
  partId: string | undefined,
  enabled: boolean,
  owner: { buyer?: string; orderNumber?: string } = {},
  componentName = "useMessagesInboxPartContext"
) {
  const { get } = useApiHelper(apiConfig(componentName));
  const query = new URLSearchParams();
  if (owner.buyer) query.set("buyer", owner.buyer);
  if (owner.orderNumber) query.set("orderNumber", owner.orderNumber);
  const queryString = query.toString() ? `?${query.toString()}` : "";

  const partContext = useQuery<IInboxPartContext>(
    messagesInboxKeys.partContext(partId, owner.buyer, owner.orderNumber),
    () => unwrap(get<DefaultResponseWithData<IInboxPartContext>>(`/messagesinbox/parts/${encodeURIComponent(partId as string)}/context${queryString}`)),
    { enabled: enabled && !!partId, staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 }
  );

  return { partContext };
}
